import {
  buildQuizCopyQuery,
  type QuizCopyContext,
} from '@/shared/tracking/quizCopySource';

/**
 * 인증을 마친 사용자가 착지하는 자리. 머무는 화면이 아니라 통로다.
 *
 * 인증은 별도 앱(`auth.zep.us`)에서 일어나고 끝나면 `callbackUrl` 로 돌려보내므로
 * quiz-app 안에 착지점이 하나 필요하다.
 *
 * ⚠️ `callbackUrl` 은 상대 경로여야 한다. auth-app 이 절대 URL 을 버린다(`redirectGuards.utils.ts`).
 */
export const buildCopyPath = (
  sourceQuizsetId: string,
  context: QuizCopyContext,
) => `/copy/${sourceQuizsetId}${buildQuizCopyQuery(context)}`;

/**
 * 착지 탭이 자기를 연 탭(원탭)에 결과를 돌려줄 때 쓰는 메시지.
 *
 * 복사는 착지 탭이 한다. 원탭은 인증 전 상태라 계정 유형을 몰라 학생 차단 판정을 할 수 없다.
 */
export const COPY_HANDOFF_MESSAGE_TYPE = 'quiz-copy-handoff' as const;

export type CopyHandoffMessage = {
  type: typeof COPY_HANDOFF_MESSAGE_TYPE;
  sourceQuizsetId: string;
} /**
 * `copiedQuizPath`: 사본 상세 주소. 인증을 마친 착지 탭이 그때 권한으로 정한다(클래스 연결 권유 키 포함 여부).
 * 원탭은 인증 전 상태라 이 판단을 할 수 없다.
 */ & (
  | { result: 'copied'; copiedQuizPath: string }
  | { result: 'blocked' }
  | { result: 'failed' }
);

/** 퀴즈 상세 주소만 받는다. 받은 주소로 이동하므로 다른 경로·외부 주소(`//host`)는 거른다. */
const isQuizDetailPath = (value: unknown): value is string =>
  typeof value === 'string' && /^\/quiz\/[\w-]+(\?[^#]*)?$/.test(value);

/** 받은 값이 우리 메시지인지 좁힌다. 오리진 검사는 수신부가 따로 하고, 여기서는 모양만 본다. */
export const isCopyHandoffMessage = (
  data: unknown,
): data is CopyHandoffMessage => {
  if (typeof data !== 'object' || data === null) return false;
  const message = data as {
    type?: unknown;
    result?: unknown;
    copiedQuizPath?: unknown;
  };
  if (message.type !== COPY_HANDOFF_MESSAGE_TYPE) return false;
  return (
    message.result !== 'copied' || isQuizDetailPath(message.copiedQuizPath)
  );
};
